<script lang="ts">
  import { page } from '$app/state'
  import { resolve } from '$app/paths'
  import { THEME } from '$lib/environment'
  import { auth } from '$lib/firebase'
  import { isAuthenticated } from '$lib/stores/authStore'
  import { logError } from '@tomra/datadog-browser-logging'
  import { signOut } from 'firebase/auth'
  import { Button, Drawer, NavBar, NavigationMenu } from '@tomra/design-system-svelte'

  let sideMenuIsOpen = $state(false)

  function toggleSideMenu() {
    sideMenuIsOpen = !sideMenuIsOpen
  }

  async function logout() {
    try {
      await signOut(auth)
    } catch (error: unknown) {
      logError(new Error('Unable to sign out user'), error as Error)
    }
  }

  const { portalName } = THEME
</script>

<NavBar class="hidden lg:flex" aria-label="Main navigation">
  <div class="flex flex-1 items-center px-8">
    <a href={resolve('/')} class="flex w-fit items-center">
      <img src={`/themes/${THEME.logoUrl}`} class="h-12" alt={`${THEME.displayName} logo`} />
      <span class="ml-4 text-lg font-medium text-gray-900 dark:text-gray-100">{portalName}</span>
    </a>
  </div>

  {#if $isAuthenticated === 'yes'}
    <NavigationMenu class="flex-1">
      <NavigationMenu.List class="gap-12">
        <NavigationMenu.Item>
          <NavigationMenu.Link href={resolve('/')} active={page.route.id?.endsWith('/(dashboard)')}>
            Dashboard
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link href={resolve('/tos')} active={page.route.id === '/tos'}>
            Terms and Conditions
          </NavigationMenu.Link>
        </NavigationMenu.Item>
      </NavigationMenu.List>
    </NavigationMenu>

    <div class="flex flex-1 items-center justify-end gap-2 px-8">
      <NavigationMenu class="flex-none">
        <NavigationMenu.List>
          <NavigationMenu.Item>
            <NavigationMenu.Link
              href={resolve('/profile')}
              active={page.route.id?.endsWith('/profile')}
              class="max-w-48 truncate text-sm"
            >
              {auth.currentUser?.displayName || auth.currentUser?.email}
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        </NavigationMenu.List>
      </NavigationMenu>

      <Button variant="icon" class="p-0" aria-label="Log out" onclick={logout}>
        <span class="icon-[fluent--sign-out-24-regular] size-5"></span>
      </Button>
    </div>
  {/if}
</NavBar>

<NavBar class="lg:hidden" aria-label="Mobile navigation">
  <Button variant="icon" aria-label="Open menu" onclick={toggleSideMenu}>
    <span class="icon-[fluent--list-24-regular] size-7"></span>
  </Button>

  <a href={resolve('/')} class="flex items-center text-base text-gray-900 dark:text-gray-100">
    <img src={`/themes/${THEME.logoUrl}`} class="h-8" alt={`${THEME.displayName} logo`} />
    <span class="ml-2 font-medium">{portalName}</span>
  </a>

  {#if $isAuthenticated === 'yes'}
    <Button variant="icon" aria-label="Log out" onclick={logout}>
      <span class="icon-[fluent--sign-out-24-regular] size-5"></span>
    </Button>
  {/if}
</NavBar>

<Drawer bind:open={sideMenuIsOpen}>
  <Drawer.Portal>
    <Drawer.Overlay />
    <Drawer.Content side="left" class="lg:hidden">
      <Drawer.Title class="sr-only">Menu</Drawer.Title>
      <Drawer.Close />
      <nav id="sideMenu">
        <ul class="grid space-y-6 p-8">
          {#if $isAuthenticated === 'yes'}
            <li>
              <a href={resolve('/')} class="text-xl" class:active={page.route.id?.endsWith('/(dashboard)')}>
                Dashboard
              </a>
            </li>
            <li>
              <a href={resolve('/tos')} class="text-xl" class:active={page.route.id === '/tos'}>
                Terms and Conditions
              </a>
            </li>
            <li>
              <a href={resolve('/profile')} class="text-xl" class:active={page.route.id?.endsWith('/profile')}>
                Profile
              </a>
            </li>
            <li class="mt-auto pt-4">
              <Button variant="outline" class="w-full" onclick={logout}>Log out</Button>
            </li>
          {/if}
        </ul>
      </nav>
    </Drawer.Content>
  </Drawer.Portal>
</Drawer>

<style lang="postcss">
  :global #sideMenu ul li .active {
    @apply underline;
  }
</style>
